import {Check, FileUploadRounded} from "@mui/icons-material";
import React, {useCallback, useMemo, useState} from "react";
import {IconButton} from "../../../features/management-v2/import/inputs";
import {usePrettyFileSize} from "../../../lib/files/usePrettyFileSize";
import {useFileSelect} from "../hooks/useFileSelect";
import BasicText from "../../../components/form-v2/basic-text";
import SaveIcon from "@mui/icons-material/Save";
import {platformAPI} from "../../../api/axiosConfig";
import {useNavigateWithClientID} from "../hooks/useNavigateWithClientID";
import {useSiren} from "../SirenContext";
import {useParams} from "react-router-dom";
import {useGetAudioFile} from "../hooks/useGetAudioFile";
import {Layout} from "../Layout";
import {ErrorMessage} from "../ErrorMessage";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import {AudioFile} from "../typedefs";
import {useUploadSirenAudio} from "../hooks/useUploadSirenAudio";
import {useAudioStreamURL} from "../hooks/useAudioStreamURL";
import {AudioPlayer} from "../../../components/ui/AudioPlayer";
import {usePermissions} from "../../../context/modulePermissions";

export const AudioEdit: React.FC = () => {
    const {id} = useParams();
    const {data, isLoading, error} = useGetAudioFile(id);

    const content = useMemo(() => {
        if (data) return <Form audioFile={data}/>;
        if (error) return <ErrorMessage text="error loading audio file"/>;
        if (isLoading) return <div className="flex justify-center p-8"><LoadingSpinner/></div>;
    }, [data, isLoading, error]);

    return (
        <Layout menuCurrent="audio">
            <div className="bg-white shadow-md p-4 flex flex-col gap-4">
                {content}
            </div>
        </Layout>
    );
};

const Form = ({audioFile}: {audioFile: AudioFile}) => {
    const {navigateWithClientID} = useNavigateWithClientID();
    const {upload, uploading, progress, error: uploadError} = useUploadSirenAudio();
    const {url: currentAudioURL} = useAudioStreamURL(audioFile.id, audioFile.updatedAt);
    const {hasFullAccess} = usePermissions();

    const [saveError, setSaveError] = useState<string | null>(null);
    const [name, setName] = useState(audioFile.name);

    const {selectedFile, invalidFile, fileInputRef, activateFileSelect, handleFileChange, fileUrl, accept} =
        useFileSelect({allowedTypes: ["mp3", "wav", "m4a"]});

    const fileSize = usePrettyFileSize(selectedFile);

    const text = useMemo(() => {
        if (invalidFile) return "Invalid File";
        return selectedFile ? `${selectedFile.name} (${fileSize})` : "Select New Audio File";
    }, [selectedFile, invalidFile, fileSize]);

    const icon = useMemo(() => selectedFile ? <Check/> : <FileUploadRounded/>, [selectedFile]);

    const canSave = !!(name.trim() && !invalidFile);
    const canEdit = hasFullAccess("siren_resources");

    const save = useCallback(async () => {
        setSaveError(null);

        let uploadID: string | undefined;
        if (selectedFile) {
            const id = await upload(selectedFile, name);
            if (!id) return;
            uploadID = id;
        }

        try {
            await platformAPI.patch(`api/siren/audio-files/${audioFile.id}`, {name, uploadID});
            navigateWithClientID("/siren/audio?subPage=files");
        } catch {
            setSaveError("error updating audio file");
        }
    }, [name, selectedFile, audioFile.id, upload]);

    return (
        <>
            <h1 className="text-lg font-semibold">{canEdit ? "Edit" : "View"} Audio File</h1>
            <BasicText label="Name" value={name} onChange={setName} required disabled={!canEdit}/>
            <label className="mb-1">Current Audio</label>
            {currentAudioURL && <AudioPlayer src={currentAudioURL}/>}
            {canEdit && (
                <div className="flex flex-col gap-4">
                    <input ref={fileInputRef} type="file" accept={accept} onChange={handleFileChange} className="hidden"/>
                    <IconButton onClick={activateFileSelect} text={text} icon={icon}/>
                    {fileUrl && <AudioPlayer src={fileUrl}/>}
                    {canSave && <IconButton onClick={save} text="Save" loading={uploading} icon={<SaveIcon/>}/>}
                    {uploading && (
                        <div className="w-full bg-light-light-grey rounded-full h-2">
                            <div className="bg-blue h-2 rounded-full transition-all duration-200" style={{width: `${progress}%`}}/>
                        </div>
                    )}
                    {uploadError && <p className="text-center text-red">{uploadError}</p>}
                    {saveError && <p className="text-center text-red">{saveError}</p>}
                </div>
            )}
        </>
    );
};
